import { FC, ReactNode, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { CopyToClipboardIcon } from 'src/components/CopyToClipboardIcon';

import classes from './detailPanel.module.css';

type BadgeConfig = {
  label: string;
  value: string;
};

type MetadataField = {
  label: string;
  value: ReactNode;
};

type DetailPanelProps = {
  /** Main title text */
  title: string;
  /** Optional ID to display on the right (e.g., Integration ID, Installation ID) */
  idLabel?: string;
  /** Optional ID value */
  idValue?: string;
  /** Description text below the title */
  description: ReactNode;
  /** Array of badges to display in the info section */
  badges: BadgeConfig[];
  /** Text for the expand button */
  expandButtonText: string;
  /** Title for the expanded detail section */
  detailTitle: string;
  /** The full object to display as JSON when expanded */
  fullObject: object;
  /** Optional metadata fields to display above the JSON */
  metadataFields?: MetadataField[];
};

/**
 * DetailPanel - A reusable component for displaying detailed information
 * with collapsible JSON content.
 *
 * Features:
 * - Title with optional ID on the right
 * - Description text
 * - Info badges (provider, name, etc.)
 * - Expandable section with metadata and JSON display
 */
export const DetailPanel: FC<DetailPanelProps> = ({
  title,
  idLabel,
  idValue,
  description,
  badges,
  expandButtonText,
  detailTitle,
  fullObject,
  metadataFields,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerSection}>
        <div className={classes.titleRow}>
          <h2 className={classes.title}>{title}</h2>
          {idLabel && idValue && (
            <div className={classes.idContainer}>
              <p className={classes.subtitle}>
                {idLabel}: {idValue}
              </p>
              <CopyToClipboardIcon textToCopy={idValue} />
            </div>
          )}
        </div>
        <p className={classes.description}>{description}</p>
      </div>

      <div className={classes.expandRow}>
        <div className={classes.infoSection}>
          {badges.map((badge) => (
            <div key={badge.label} className={classes.badge}>
              <span className={classes.badgeLabel}>{badge.label}</span>
              <span className={classes.badgeValue}>{badge.value}</span>
            </div>
          ))}
        </div>
        <div
          className={classes.expandButton}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="button"
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details`}
        >
          <span className={classes.expandText}>{expandButtonText}</span>
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </div>

      {isExpanded && (
        <div className={classes.detailContent}>
          <div className={classes.detailHeader}>
            <h4>{detailTitle}</h4>
          </div>
          {metadataFields && metadataFields.length > 0 && (
            <div className={classes.metadataSection}>
              {metadataFields.map((field) => (
                <div key={field.label} className={classes.metadataField}>
                  <span className={classes.metadataLabel}>{field.label}</span>
                  <span className={classes.metadataValue}>{field.value}</span>
                </div>
              ))}
            </div>
          )}
          <div className={classes.jsonBlockWrap}>
            <span className={classes.jsonBlockCopy}>
              <CopyToClipboardIcon
                id="detail-json-copy"
                tooltipClassName={classes.jsonCopyTooltip}
                textToCopy={JSON.stringify(fullObject, null, 2)}
              />
            </span>
            <pre className={classes.jsonBlock}>{JSON.stringify(fullObject, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
};
